import { z } from "zod";
export const WORKFLOW_EVENT_TYPES = [
    "job.created.v1",
    "job.status_changed.v1",
    "appointment.status_changed.v1",
    "appointment.rescheduled.v1",
    "estimate.sent.v1",
    "estimate.viewed.v1",
    "estimate.accepted.v1",
    "invoice.issued.v1",
    "invoice.viewed.v1",
    "invoice.paid.v1",
    "payment.failed.v1",
    "customer.created.v1",
];
export const AUTOMATIONS_V1_EVENT_TYPES = ["job.status_changed.v1"];
export const AUTOMATIONS_V1_ACTION_KINDS = ["INTERNAL_NOTIFICATION"];
export const ACTION_LABELS = {
    INTERNAL_NOTIFICATION: "Notify the office",
    SEND_EMAIL: "Send email",
    SEND_SMS: "Send SMS",
    ADD_TAG: "Add tag",
    UPDATE_JOB_STATUS: "Update job status",
    MOVE_PIPELINE_STAGE: "Move pipeline stage",
    WEBHOOK: "Call webhook",
};
export const EVENT_LABELS = {
    "job.created.v1": "Job created",
    "job.status_changed.v1": "Job status changed",
    "appointment.status_changed.v1": "Appointment status changed",
    "appointment.rescheduled.v1": "Appointment rescheduled",
    "estimate.sent.v1": "Estimate sent",
    "estimate.viewed.v1": "Estimate viewed",
    "estimate.accepted.v1": "Estimate accepted",
    "invoice.issued.v1": "Invoice issued",
    "invoice.viewed.v1": "Invoice viewed",
    "invoice.paid.v1": "Invoice paid",
    "payment.failed.v1": "Payment failed",
    "customer.created.v1": "Customer created",
};
export const WORKFLOW_TEMPLATES = [
    {
        id: "estimate-follow-up",
        name: "Estimate follow-up",
        description: "Wait one day after an estimate is viewed, then follow up.",
        eventType: "estimate.viewed.v1",
        actionLabel: "Send follow-up email",
        create: () => linearTemplate(triggerNode("trigger", "estimate.viewed.v1", 60, 110), delayNode("delay", 1, "DAYS", 360, 110), actionNode("action", "SEND_EMAIL", "customer.primaryEmail", "Thanks for reviewing your estimate. Reply if we can help.", 660, 110)),
    },
    {
        id: "payment-recovery",
        name: "Payment needs attention",
        description: "Alert the office immediately when a payment fails.",
        eventType: "payment.failed.v1",
        actionLabel: "Notify the office",
        create: () => linearTemplate(triggerNode("trigger", "payment.failed.v1", 80, 130), actionNode("action", "INTERNAL_NOTIFICATION", "business.admins", "Payment failed and needs review.", 410, 130)),
    },
    {
        id: "completed-job-office-notification",
        name: "Completed job notification",
        description: "Notify the office shortly after a job is completed.",
        eventType: "job.status_changed.v1",
        actionLabel: "Send review request",
        create: () => ({
            nodes: [
                triggerNode("trigger", "job.status_changed.v1", 40, 120),
                conditionNode("condition", "data.toStatus", "EQUALS", "COMPLETED", 330, 120),
                delayNode("delay", 5, "MINUTES", 640, 50),
                actionNode("action", "INTERNAL_NOTIFICATION", "business.admins", "Job {{data.jobId}} was completed and is ready for office review.", 930, 50),
            ],
            edges: [
                edge("trigger", "condition"),
                edge("condition", "delay", "TRUE"),
                edge("delay", "action"),
            ],
        }),
    },
];
export function createWorkflow(name = "Untitled workflow", template) {
    const now = new Date().toISOString();
    const graph = template?.create() ?? {
        nodes: [triggerNode("trigger", "job.status_changed.v1", 80, 160)],
        edges: [],
    };
    return {
        id: createId("workflow"),
        name: template?.name ?? name,
        description: template?.description ?? "",
        status: "DRAFT",
        version: 0,
        createdAt: now,
        updatedAt: now,
        nodes: graph.nodes,
        edges: graph.edges,
        publishedVersions: [],
    };
}
export function validateWorkflow(workflow) {
    const issues = [];
    const nodeIds = new Set(workflow.nodes.map((node) => node.id));
    const triggers = workflow.nodes.filter((node) => node.config.kind === "TRIGGER");
    if (triggers.length !== 1) {
        issues.push({
            code: "TRIGGER_COUNT",
            message: "A workflow must contain exactly one trigger.",
        });
    }
    if (!workflow.nodes.some((node) => node.config.kind === "ACTION")) {
        issues.push({
            code: "ACTION_REQUIRED",
            message: "Add at least one action before publishing.",
        });
    }
    for (const edgeValue of workflow.edges) {
        if (!nodeIds.has(edgeValue.source) || !nodeIds.has(edgeValue.target)) {
            issues.push({
                code: "BROKEN_EDGE",
                message: "A connection points to a missing workflow step.",
            });
        }
    }
    if (triggers[0]) {
        const reachable = reachableNodeIds(triggers[0].id, workflow.edges);
        for (const node of workflow.nodes) {
            if (!reachable.has(node.id)) {
                issues.push({
                    code: "UNREACHABLE_NODE",
                    message: `${node.label} is not connected to the trigger.`,
                    nodeId: node.id,
                });
            }
        }
    }
    if (containsCycle(workflow.nodes, workflow.edges)) {
        issues.push({
            code: "CYCLE_NOT_SUPPORTED",
            message: "Workflow loops are not supported. Remove the circular connection.",
        });
    }
    for (const node of workflow.nodes) {
        if (node.config.kind === "CONDITION" && !node.config.field.trim()) {
            issues.push({
                code: "CONDITION_FIELD_REQUIRED",
                message: "Choose an event field for this condition.",
                nodeId: node.id,
            });
        }
        if (node.config.kind === "DELAY" && node.config.amount < 1) {
            issues.push({
                code: "DELAY_INVALID",
                message: "Delay must be at least one unit.",
                nodeId: node.id,
            });
        }
        if (node.config.kind === "ACTION" && !node.config.target.trim()) {
            issues.push({
                code: "ACTION_TARGET_REQUIRED",
                message: "Choose a target for this action.",
                nodeId: node.id,
            });
        }
    }
    return issues;
}
export function publishWorkflow(workflow) {
    const issues = validateWorkflow(workflow);
    if (issues.length)
        throw new WorkflowValidationError(issues);
    const now = new Date().toISOString();
    const version = workflow.version + 1;
    return {
        ...workflow,
        status: "ACTIVE",
        version,
        updatedAt: now,
        publishedVersions: [
            ...workflow.publishedVersions,
            {
                version,
                publishedAt: now,
                nodes: clone(workflow.nodes),
                edges: clone(workflow.edges),
            },
        ],
    };
}
export class WorkflowValidationError extends Error {
    issues;
    name = "WorkflowValidationError";
    constructor(issues) {
        super(issues[0]?.message ?? "Workflow is invalid.");
        this.issues = issues;
    }
}
export function createNode(kind, position) {
    const id = createId(kind.toLowerCase());
    switch (kind) {
        case "TRIGGER":
            return triggerNode(id, "job.status_changed.v1", position.x, position.y);
        case "CONDITION":
            return conditionNode(id, "data.toStatus", "EQUALS", "COMPLETED", position.x, position.y);
        case "DELAY":
            return delayNode(id, 1, "HOURS", position.x, position.y);
        case "ACTION":
            return actionNode(id, "INTERNAL_NOTIFICATION", "business.admins", "Workflow action completed.", position.x, position.y);
    }
}
export function createId(prefix) {
    return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
}
function triggerNode(id, eventType, x, y) {
    return {
        id,
        label: EVENT_LABELS[eventType],
        position: { x, y },
        config: { kind: "TRIGGER", eventType },
    };
}
function conditionNode(id, field, operator, value, x, y) {
    return {
        id,
        label: "Check a condition",
        position: { x, y },
        config: { kind: "CONDITION", field, operator, value },
    };
}
function delayNode(id, amount, unit, x, y) {
    return {
        id,
        label: "Wait",
        position: { x, y },
        config: { kind: "DELAY", amount, unit },
    };
}
function actionNode(id, action, target, message, x, y) {
    return {
        id,
        label: ACTION_LABELS[action],
        position: { x, y },
        config: { kind: "ACTION", action, target, message },
    };
}
function edge(source, target, branch) {
    return {
        id: `${source}-${branch ?? "next"}-${target}`,
        source,
        target,
        branch,
    };
}
function linearTemplate(...nodes) {
    return {
        nodes,
        edges: nodes.slice(1).map((node, index) => edge(nodes[index].id, node.id)),
    };
}
function reachableNodeIds(start, edges) {
    const reached = new Set([start]);
    const queue = [start];
    while (queue.length) {
        const current = queue.shift();
        for (const edgeValue of edges.filter((item) => item.source === current)) {
            if (!reached.has(edgeValue.target)) {
                reached.add(edgeValue.target);
                queue.push(edgeValue.target);
            }
        }
    }
    return reached;
}
function containsCycle(nodes, edges) {
    const visiting = new Set();
    const visited = new Set();
    const visit = (nodeId) => {
        if (visiting.has(nodeId))
            return true;
        if (visited.has(nodeId))
            return false;
        visiting.add(nodeId);
        for (const edgeValue of edges.filter((item) => item.source === nodeId)) {
            if (visit(edgeValue.target))
                return true;
        }
        visiting.delete(nodeId);
        visited.add(nodeId);
        return false;
    };
    return nodes.some((node) => visit(node.id));
}
function clone(value) {
    return JSON.parse(JSON.stringify(value));
}
const workflowEventTypeSchema = z.enum(WORKFLOW_EVENT_TYPES);
const workflowStatusSchema = z.enum(["DRAFT", "ACTIVE", "PAUSED"]);
const positionSchema = z.object({ x: z.number(), y: z.number() }).strict();
const triggerConfigSchema = z
    .object({ kind: z.literal("TRIGGER"), eventType: workflowEventTypeSchema })
    .strict();
const conditionConfigSchema = z
    .object({
    kind: z.literal("CONDITION"),
    field: z.string().trim().min(1).max(240),
    operator: z.enum(["EQUALS", "NOT_EQUALS", "CONTAINS", "EXISTS"]),
    value: z.string().max(500),
})
    .strict();
const delayConfigSchema = z
    .object({
    kind: z.literal("DELAY"),
    amount: z.number().int().min(1).max(365),
    unit: z.enum(["MINUTES", "HOURS", "DAYS"]),
})
    .strict();
const actionConfigSchema = z
    .object({
    kind: z.literal("ACTION"),
    action: z.enum([
        "INTERNAL_NOTIFICATION",
        "SEND_EMAIL",
        "SEND_SMS",
        "ADD_TAG",
        "UPDATE_JOB_STATUS",
        "MOVE_PIPELINE_STAGE",
        "WEBHOOK",
    ]),
    target: z.string().trim().min(1).max(240),
    message: z.string().max(4_000),
})
    .strict();
const nodeConfigSchema = z.discriminatedUnion("kind", [
    triggerConfigSchema,
    conditionConfigSchema,
    delayConfigSchema,
    actionConfigSchema,
]);
const workflowNodeSchema = z
    .object({
    id: z.string().min(1).max(160),
    label: z.string().min(1).max(240),
    position: positionSchema,
    config: nodeConfigSchema,
})
    .strict();
const workflowEdgeSchema = z
    .object({
    id: z.string().min(1).max(160),
    source: z.string().min(1).max(160),
    target: z.string().min(1).max(160),
    branch: z.enum(["TRUE", "FALSE"]).optional(),
})
    .strict();
const publishedWorkflowVersionSchema = z
    .object({
    version: z.number().int().positive(),
    publishedAt: z.string().datetime({ offset: true }),
    nodes: z.array(workflowNodeSchema).max(100),
    edges: z.array(workflowEdgeSchema).max(200),
})
    .strict();
export const workflowDefinitionSchema = z
    .object({
    id: z.string().min(1).max(160),
    name: z.string().trim().min(1).max(160),
    description: z.string().max(1_000),
    status: workflowStatusSchema,
    version: z.number().int().nonnegative(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    nodes: z.array(workflowNodeSchema).max(100),
    edges: z.array(workflowEdgeSchema).max(200),
    publishedVersions: z.array(publishedWorkflowVersionSchema).max(100),
})
    .strict();
export const workflowExecutionSchema = z
    .object({
    id: z.string().min(1),
    workflowId: z.string().min(1),
    workflowName: z.string().min(1),
    workflowVersion: z.number().int().nonnegative(),
    triggerEventId: z.string().min(1),
    triggerEventType: workflowEventTypeSchema,
    status: z.enum([
        "RUNNING",
        "WAITING",
        "SUCCEEDED",
        "FAILED",
        "DEAD_LETTER",
    ]),
    test: z.boolean(),
    startedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    steps: z.array(z
        .object({
        nodeId: z.string().min(1),
        label: z.string().min(1),
        status: z.enum([
            "PENDING",
            "RUNNING",
            "WAITING",
            "SUCCEEDED",
            "SKIPPED",
            "RETRY_SCHEDULED",
            "FAILED",
            "DEAD_LETTER",
        ]),
        detail: z.string(),
        completedAt: z.string().datetime({ offset: true }),
        attempt: z.number().int().positive().optional(),
        nextAttemptAt: z
            .string()
            .datetime({ offset: true })
            .nullable()
            .optional(),
    })
        .strict()),
})
    .strict();
export const automationsBootstrapSchema = z
    .object({
    workflows: z.array(workflowDefinitionSchema),
    executions: z.array(workflowExecutionSchema),
    notifications: z.array(z
        .object({
        id: z.string().min(1),
        executionId: z.string().min(1),
        title: z.string().min(1),
        message: z.string(),
        target: z.string().min(1),
        createdAt: z.string().datetime({ offset: true }),
        readAt: z.string().datetime({ offset: true }).nullable(),
    })
        .strict()),
    connection: z
        .object({
        eventEndpointUrl: z.string().url(),
        secretConfigured: z.boolean(),
        subscribedEventTypes: z.array(workflowEventTypeSchema),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=index.js.map